// src/components/BumpupDashboard.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './bumpupDashboard.css';

// FIREBASE
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// FUNCTIONS
import { uploadBump } from '../uploadBump';
import AppTransition from '../../../components/appComponents/AppTransition';


// IMPORT UI
import ListViewBumpUp from './listviewBumpUp';
import ConnectedAccounts from './ConnectedAccounts';
import InsufficientTimePopUp from '../../../components/miscComponents/InsufficientTimePopUp';
import UserStudio from '../../UserStudio';
import PlanPurchasePage from '../../../myPlans/PlanPurchasePage';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCircleArrowUp, faXmark } from '@fortawesome/free-solid-svg-icons';

// IMAGES
import placeholder_dark_thumbnail from '../../../assets/images/thumbnail/placeholder-dark-thumbnail.jpg';
import placeholder_profile from '../../../assets/images/profile/placeholder-profile.jpg';
import youtube_white_logo from '../../../assets/images/logo/youtube-white-logo.png';
import youtube_logo from '../../../assets/images/logo/youtube-logo.png';
import processBumpupslogo from '../../../assets/gifmedia/bumpups-2.gif';

export default function BumpupDashboard() {
  const [insufficientTime, setInsufficientTime] = useState(false);

  const [profileImage, setProfileImage] = useState(placeholder_profile);
  const [accessToken, setAccessToken] = useState(null);

  // Out-of-minutes flag, avatar and YouTube connection, all live
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const unsubs = [
      onSnapshot(doc(db, 'users', user.uid, 'subscriptionData', 'noTimeAvailable'), snap => {
        setInsufficientTime(!!snap.data()?.insufficientTime);
      }, () => {}),
      onSnapshot(doc(db, 'users', user.uid, 'userSettings', 'profile'), snap => {
        setProfileImage(snap.data()?.profileImage || placeholder_profile);
      }, () => {}),
      onSnapshot(doc(db, 'users', user.uid, 'contentAccess', 'youtubeAccess'), snap => {
        setAccessToken(snap.data()?.accessToken || null);
      }, () => {}),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  return (
    <div className="bumps-uploaddashboard">
      {/* show popup whenever subscription time is insufficient */}
      {insufficientTime && <InsufficientTimePopUp />}

      <AppTransition>
        <UserStudio />
        <div className="bumps-dashboard-content-wrapper">
          <div className="bumps-dashboard-contentgeneral">
            <ConnectedAccounts
              profileImage={profileImage}
              accessToken={accessToken}
            />
            <UploadSection
              profileImage={profileImage}
              accessToken={accessToken}
            />
          </div>
          <ListViewBumpUp accessToken={accessToken} />
        </div>
      </AppTransition>
    </div>
  );
}

function UploadSection({ profileImage, accessToken }) {
  // --- plan & credits state ---
  const [currentPlan, setCurrentPlan] = useState('free');
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsLimit, setCreditsLimit] = useState(0);
  const [showPlanPopUp, setShowPlanPopUp] = useState(false);

  // --- upload state ---
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [showJustUploadedPopup, setShowJustUploadedPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoId, setVideoId] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [showInsufficientTimePopup, setShowInsufficientTimePopup] = useState(false);
  const [videoDurationSec, setVideoDurationSec] = useState(null);

  const fileInputRef   = useRef(null);
  const uploadTaskRef  = useRef(null);
  const storagePathRef = useRef('');

  const MAX_FILE_SIZE_GB    = 16;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_GB * 1024 ** 3;

  // derived flags
  const isPro = currentPlan.toLowerCase() === 'pro';
  const remainingCredits = Math.max(creditsLimit - creditsUsed, 0);
  const canUpload = isPro && remainingCredits > 0;

  // 1) live plan + credits
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid, 'subscriptionData', 'planDetails'), snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      setCurrentPlan((data.currentPlan || 'free').toString().toLowerCase());
      setCreditsUsed(data.creditsUsed ?? 0);
      setCreditsLimit(data.creditsLimit ?? 0);
    }, () => {});
  }, []);

  // 2) handleUpload must be declared *before* the drag-and-drop useEffect
  const handleUpload = useCallback(async event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert(`The selected video exceeds ${MAX_FILE_SIZE_GB} GB. Please choose a smaller file.`);
      return;
    }

    // measure duration
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.onloadedmetadata = () => {
      window.URL.revokeObjectURL(videoEl.src);
      const duration = videoEl.duration;
      setVideoDurationSec(duration);

      const availableSec = remainingCredits * 60;
      if (duration > availableSec) {
        setShowInsufficientTimePopup(true);
        return;
      }

      // proceed with upload
      setSelectedFile(file);
      setShowJustUploadedPopup(true);

      const user = auth.currentUser;
      uploadBump(
        file,
        user,
        setUploadProgress,
        setVideoId,
        setShowPopup,
        () => {},
        storagePathRef,
        uploadTaskRef,
        thumbnailUrl
      );
      setShowJustUploadedPopup(false);
      event.target.value = null;
    };
    videoEl.src = URL.createObjectURL(file);
  }, [remainingCredits, thumbnailUrl, MAX_FILE_SIZE_BYTES]);

  // 3) drag-and-drop effect, now gated on `canUpload`
  useEffect(() => {
    const dropArea = document.querySelector('.bumps-file-drop-area');
    if (!dropArea) return;

    const onDragOver = e => {
      e.preventDefault(); e.stopPropagation();
      if (!canUpload) return;
      const it = e.dataTransfer.items;
      if (it.length > 0 && it[0].kind === 'file' && it[0].type.startsWith('video/')) {
        dropArea.classList.add('drag-over');
      }
    };
    const onDragLeave = e => {
      e.preventDefault(); e.stopPropagation();
      if (!canUpload) return;
      dropArea.classList.remove('drag-over');
    };
    const onDrop = e => {
      e.preventDefault(); e.stopPropagation();
      if (!canUpload) return;
      dropArea.classList.remove('drag-over');
      const file = e.dataTransfer.files[0];
      if (file) handleUpload({ target: { files: [file] } });
    };

    dropArea.addEventListener('dragover', onDragOver);
    dropArea.addEventListener('dragleave', onDragLeave);
    dropArea.addEventListener('drop', onDrop);
    return () => {
      dropArea.removeEventListener('dragover', onDragOver);
      dropArea.removeEventListener('dragleave', onDragLeave);
      dropArea.removeEventListener('drop', onDrop);
    };
  }, [canUpload, handleUpload]);

  // trigger input click
  const triggerFileInput = () => fileInputRef.current?.click();

  return (
    <div className="bumps-upload-section">
      <div className={`bumps-upload-area ${!canUpload ? 'disabled' : ''}`}>
        <label htmlFor="fileInput" className="bumps-file-input-label">
          <div className="bumps-file-drop-area">
            <input
              type="file"
              id="fileInput"
              ref={fileInputRef}
              accept="video/mp4,video/mov,video/webm,video/quicktime"
              onChange={handleUpload}
              hidden
              disabled={!canUpload}
            />

            <div className="bumps-upload-circle">
              <FontAwesomeIcon
                icon={faCircleArrowUp}
                className="bumps-upload-logo"
                title="Upload"
              />
            </div>

            {!isPro ? (
              <>
                <p className="bumps-upload-instruction">
                  You need the <b>Pro plan</b> to use this feature.
                </p>
                <p className="bumps-upload-subtext">
                  Upgrade to enable local video uploads, social media scheduling, and more for content creators.
                </p>
              </>
            ) : remainingCredits === 0 ? (
              <>
                <p className="bumps-upload-instruction">
                  You are currently out of credits for this month
                </p>
                <p className="bumps-upload-subtext">
                  Next usage cycle you will receive your plan minutes.
                </p>
              </>
            ) : (
              <>
                <p className="bumps-upload-instruction">
                  Drag and drop video files to upload
                </p>
                <p className="bumps-upload-subtext">
                  Your videos will be private until you publish them.
                </p>
              </>
            )}

            {(!isPro || remainingCredits === 0) ? (
              <button
                className="bumps-select-files-button"
                onClick={() => setShowPlanPopUp(true)}
              >
                View Plan
              </button>
            ) : (
              <button
                className="bumps-select-files-button"
                onClick={triggerFileInput}
              >
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                Select files
              </button>
            )}
          </div>
        </label>
      </div>

      {showJustUploadedPopup && <JustUploadedPopup />}
      {showPopup && (
        <UploadPopup
          progress={uploadProgress}
          file={selectedFile}
          onClose={() => setShowPopup(false)}
          uploadTaskRef={uploadTaskRef}
          storagePathRef={storagePathRef}
          videoId={videoId}
          setThumbnailUrl={setThumbnailUrl}
          profileImage={profileImage}
          accessToken={accessToken}
          videoDurationSec={videoDurationSec}
          remainingCredits={remainingCredits}
        />
      )}
      {showInsufficientTimePopup && (
        <InsufficientTimePopUp
          onClose={() => setShowInsufficientTimePopup(false)}
          neededMinutes={Math.max(Math.floor((videoDurationSec || 0) / 60), 1)}
          remainingMinutes={remainingCredits}
        />
      )}

      {showPlanPopUp && (
        <PlanPurchasePage
          source="studio"
          onClose={() => setShowPlanPopUp(false)}
        />
      )}
    </div>
  );
}

function UploadPopup({
  progress,
  file,
  onClose,
  uploadTaskRef,
  storagePathRef,
  videoId,
  setThumbnailUrl,
  profileImage,
  accessToken,
  videoDurationSec,
  remainingCredits
}) {
  const [videoUrl, setVideoUrl] = useState('');
  const [videoDurationExists, setVideoDurationExists] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [localThumbnailUrl, setLocalThumbnailUrl] = useState('');
  const [uploadError, setUploadError] = useState(false);
  const [displayedProgress, setDisplayedProgress] = useState(progress);
  const [uploadStatusText, setUploadStatusText] = useState(`Uploading (${progress}%)`);
  const [isOutOfTime, setIsOutOfTime] = useState(false);

  const user = auth.currentUser;
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // preview video
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // progress text
  useEffect(() => {
    if (progress < 98) {
      setDisplayedProgress(progress);
      setUploadStatusText(`Uploading (${progress}%)`);
    } else if (progress < 100) {
      setDisplayedProgress(98);
      setUploadStatusText(`Uploading (98%)`);
    } else {
      setDisplayedProgress(100);
      setUploadStatusText('Finalizing Upload...');
    }
  }, [progress]);

  // cancel if credits drop to zero mid-upload
  useEffect(() => {
    const availableSec = remainingCredits * 60;
    if (videoDurationSec > availableSec && progress < 100) {
      setIsOutOfTime(true);
      uploadTaskRef.current?.cancel();
    } else {
      setIsOutOfTime(false);
    }
  }, [videoDurationSec, progress, remainingCredits, uploadTaskRef]);

  // watch firestore for video duration field
  useEffect(() => {
    if (!user || !videoId) return;
    const videoDocRef = doc(db, 'users', user.uid, 'bumpContent', `bump-${videoId}`);
    const unsub = onSnapshot(videoDocRef, snap => {
      if (snap.exists()) {
        const exists = 'video_duration_in_seconds' in snap.data();
        setVideoDurationExists(exists);
        if (exists) setUploadStatusText('Upload Complete');
      }
    });
    return () => unsub();
  }, [user, videoId]);

  const handleCancelUpload = () => {
    if (progress === 100) {
      const key = `videoCache_${user.uid}_BumpedVideos`;
      localStorage.removeItem(key);
      onClose();
      window.location.reload();
    } else {
      setShowConfirmation(true);
    }
  };

  const handleConfirmClose = async () => {
    if (progress < 100) {
      uploadTaskRef.current?.cancel();
    }
    if (storagePathRef.current) {
      try {
        await deleteObject(storagePathRef.current);
      } catch {}
    }
    setShowConfirmation(false);
    onClose();
  };

  const handleCancelClose = () => setShowConfirmation(false);

  const handleThumbnailUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg'];
    const maxSize = 1 * 1024 * 1024;
    if (!validTypes.includes(file.type) || file.size > maxSize) {
      alert('Please upload a valid PNG/JPEG under 1MB.');
      return;
    }
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const { width, height } = img;
      const aspect = width / height;
      const validDim =
        (width === 1280 && height === 720) ||
        (width >= 640 && height >= 360 && aspect === 16 / 9);
      if (!validDim) {
        alert('Thumbnail must be 1280x720 or min 640x360 at 16:9.');
        return;
      }
      setLocalThumbnailUrl(placeholder_dark_thumbnail);

      const thumbRef = ref(storage, `bumpThumbnails/${user.uid}/${videoId}`);
      const task = uploadBytesResumable(thumbRef, file);
      task.on(
        'state_changed',
        null,
        () => {},
        () => {
          getDownloadURL(task.snapshot.ref).then(url => {
            setLocalThumbnailUrl(url);
            setThumbnailUrl(url);
          });
        }
      );
    };
  };

  const handleViewVideo = () => {
    const key = `videoCache_${user.uid}_BumpedVideos`;
    localStorage.removeItem(key);
    navigate(`/bumper/${videoId}`);
  };

  useEffect(() => {
    const onError = () => setUploadError(true);
    uploadTaskRef.current?.on('state_changed', null, onError);
  }, [uploadTaskRef]);

  useEffect(() => {
    const onBefore = e => {
      if (progress < 100) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [progress]);

  const truncateFileName = name =>
    name.length > 28 ? `${name.slice(0, 28)}...` : name;

  return (
    <div className="bumps-upload-popup">
      <div className="bumps-upload-popup-content">
        <div className="bumps-upload-header">
          <img src={profileImage} alt="User Profile" className="bumps-prevprofile-image" />
          <button onClick={handleCancelUpload} className="bumps-upload-close-button" aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="bumps-upload-content-wrapper">
          <div className="bumps-video-wrapper" style={{ position: 'relative' }}>
            <video src={videoUrl} controls className="bumps-upload-video" />
          </div>
          <div className="bumps-upload-info-wrapper">
            <div className="bumps-upload-info">
              <p className="bumps-upload-message">
                Keep your browser tab open to avoid interruptions. You may cancel anytime
                but if your video reaches 100% uploaded, your plan credits will be deducted.
              </p>
              <a
                href="/help/articles/9742215-how-does-the-bump-video-upload-process-work"
                className="bumps-learn-more-link"
              >
                Learn more
              </a>
            </div>
          </div>
          <div className={`bumps-upload-status-wrapper ${uploadError || isOutOfTime ? 'error' : ''}`}>
            <div className="bumps-upload-status">
              <p className="bumps-upload-progress">
                <strong>
                  {truncateFileName(file.name)}:{' '}
                  {isOutOfTime
                    ? 'You are out of credits'
                    : uploadError
                    ? 'Error Uploading -- Retry'
                    : uploadStatusText}
                </strong>
              </p>
            </div>
          </div>
        </div>
        <div className="bumps-divider" />
        <div className="bumps-upload-buttons">
          {accessToken ? (
            <img src={youtube_logo} alt="YouTube Icon" className="utubedistribution-icon-active" />
          ) : (
            <img src={youtube_white_logo} alt="YouTube Icon" className="utubedistribution-icon" />
          )}
          <div className="bumps-buttons-right">
            {localThumbnailUrl && (
              <img src={localThumbnailUrl} alt="Thumbnail" className="bumps-thumbnail-small" />
            )}
            <input
              type="file"
              accept="image/png, image/jpeg"
              onChange={handleThumbnailUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <button
              className={`bumps-bump-button ${displayedProgress < 100 || !videoDurationExists ? 'disabled' : ''}`}
              disabled={displayedProgress < 100 || !videoDurationExists}
              onClick={handleViewVideo}
            >
              Edit
            </button>
          </div>
        </div>
      </div>
      {showConfirmation && (
        <CancelUploadPopup
          onConfirm={handleConfirmClose}
          onCancel={handleCancelClose}
          isDiscarding={false}
        />
      )}
    </div>
  );
}

function JustUploadedPopup() {
  return (
    <div className="just-uploaded-popup">
      <div className="just-uploaded-popup-content">
        <div className="bumps-loading">
          <img src={processBumpupslogo} alt="Uploading" className="bumps-loading-logo" />
          <p className="bumps-loading-text">Loading...</p>
        </div>
      </div>
    </div>
  );
}

function CancelUploadPopup({ onConfirm, onCancel, isDiscarding }) {
  return (
    <div className="cancel-upload-popup" onClick={onCancel}>
      <div className="cancel-upload-popup-content" onClick={e => e.stopPropagation()}>
        {!isDiscarding && <h2 className="cancel-upload-title">Hold that thought</h2>}
        {isDiscarding ? (
          <div className="bumps-loading">
            <img src={processBumpupslogo} alt="Loading" className="bumps-loading-logo" />
            <span className="bumps-loading-text">Deleting...</span>
          </div>
        ) : (
          <p className="cancel-upload-message">
            We are still uploading your video. Are you sure you want to discard your post? Your draft will be lost.
          </p>
        )}
        {!isDiscarding && (
          <div className="cancel-upload-popup-buttons">
            <button onClick={onCancel} className="bumps-continue-button">Continue</button>
            <button onClick={onConfirm} className="bumps-discard-button">Discard</button>
          </div>
        )}
      </div>
    </div>
  );
}
